import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Input, Select, Button, Table, Checkbox, Modal, message,
  Row, Col, Space, Tag, Tabs, Divider, Alert, Tooltip,
} from 'antd';
import { SendOutlined, EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { loiAPI, tenderAPI } from '../services/api';

const { Option } = Select;
const { TextArea } = Input;

const TEMPLATE_VARIABLES = [
  { key: '{{dealer_name}}', desc: '經銷商名稱' },
  { key: '{{company_name}}', desc: '公司名稱' },
  { key: '{{contact_person}}', desc: '聯絡人' },
  { key: '{{case_number}}', desc: '案號' },
  { key: '{{case_title}}', desc: '案件名稱' },
  { key: '{{survey_link}}', desc: '意向書填寫連結' },
  { key: '{{price_link}}', desc: '報價填寫連結' },
];

const SAMPLE_DATA = {
  '{{dealer_name}}': '範例科技有限公司',
  '{{company_name}}': '範例科技有限公司',
  '{{contact_person}}': '王大明',
  '{{case_number}}': '1150201',
  '{{case_title}}': '115年第一次電腦軟體共同供應契約採購-套裝軟體',
  '{{survey_link}}': 'https://example.com/survey/abc123',
  '{{price_link}}': 'https://example.com/prices/abc123',
};

// Pre-built email templates
const EMAIL_TEMPLATES = {
  loi_initial: {
    name: 'LOI 意向書通知（首次發送）',
    subject: '宇聯科技_{{case_title}} (案號{{case_number}}) 意向書調查',
    body: `<html>
<body style="font-family:'Microsoft JhengHei','Noto Sans TC',Arial,sans-serif;margin:0;padding:0;background:#f5f5f5;">
<div style="max-width:680px;margin:20px auto;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.1);">
  <div style="background-color:#001529;padding:30px;text-align:center;">
    <h1 style="color:#ffffff;margin:0;font-size:22px;">宇聯科技股份有限公司</h1>
    <p style="color:#89CFF0;margin:8px 0 0;font-size:14px;">Comboware / GraphOn Corp 總代理商</p>
  </div>
  <div style="padding:30px;">
    <p style="font-size:16px;"><strong>{{company_name}}</strong> {{contact_person}} 您好！</p>
    <p style="line-height:1.8;">
      今年度「<strong>{{case_title}}</strong>」（案號 <strong>{{case_number}}</strong>），
      本公司宇聯科技參與品牌及項次更新如下，請務必留意：
    </p>
    <div style="background:#f9f9f9;padding:16px;border-radius:6px;margin:20px 0;border-left:4px solid #0056b3;">
      <strong>【報價與說明】</strong><br/>
      報價單皆含稅並同公告底價，詳細產品清單與型號請參閱郵件附件。
    </div>
    <div style="background:#fff7e6;padding:16px;border-radius:6px;margin:20px 0;border-left:4px solid #faad14;">
      <strong>【意向書填寫】</strong><br/>
      本意向書目的為事先確認投標意願，請於期限內點擊下方按鈕完成填寫，
      本公司於投標前會根據意向書內容給予『<strong>價格承諾書</strong>』。
    </div>
    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:30px 0;">
      <tr><td align="center">
        <table cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td align="center" bgcolor="#0056b3" style="border-radius:6px;mso-padding-alt:14px 40px;" height="48">
              <a href="{{survey_link}}" target="_blank" style="display:inline-block;padding:14px 40px;color:#ffffff;font-size:16px;font-weight:bold;text-decoration:none;font-family:Arial,sans-serif;line-height:20px;mso-line-height-rule:exactly;">
                前往填寫意向書
              </a>
            </td>
          </tr>
        </table>
      </td></tr>
    </table>
    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:10px 0 30px;">
      <tr><td align="center">
        <table cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td align="center" style="border:2px solid #0056b3;border-radius:6px;mso-padding-alt:10px 30px;" height="40">
              <a href="{{price_link}}" target="_blank" style="display:inline-block;padding:10px 30px;color:#0056b3;font-size:14px;font-weight:bold;text-decoration:none;font-family:Arial,sans-serif;line-height:18px;mso-line-height-rule:exactly;">
                查看產品公告底價清單
              </a>
            </td>
          </tr>
        </table>
      </td></tr>
    </table>
    <hr style="border:none;border-top:1px solid #e8e8e8;margin:30px 0;"/>
    <p style="font-size:14px;color:#666;line-height:1.8;">
      如有任何疑問，請洽專案連絡人：<br/>
      周伶瑋 (Jolie) 02-8773-0263 #181 /
      <a href="mailto:jolie.chou@ulink.com.tw">jolie.chou@ulink.com.tw</a><br/>
      蘇鈺涵 (Anny) 02-8773-0263 #182 /
      <a href="mailto:anny.su@ulink.com.tw">anny.su@ulink.com.tw</a>
    </p>
    <p style="margin-top:20px;"><strong>宇聯科技 敬上</strong></p>
  </div>
  <div style="background:#f5f5f5;padding:16px;text-align:center;font-size:12px;color:#999;">
    此信件由系統自動發送，請勿直接回覆。如需聯絡請洽上述專案窗口。
  </div>
</div>
</body>
</html>`,
  },
  loi_reminder: {
    name: 'LOI 意向書催寄（提醒）',
    subject: '【提醒】宇聯科技_{{case_title}} (案號{{case_number}}) 意向書調查',
    body: `<html>
<body style="font-family:'Microsoft JhengHei','Noto Sans TC',Arial,sans-serif;margin:0;padding:0;background:#f5f5f5;">
<div style="max-width:680px;margin:20px auto;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.1);">
  <div style="background-color:#cf1322;padding:30px;text-align:center;">
    <h1 style="color:#ffffff;margin:0;font-size:22px;">意向書填寫提醒</h1>
    <p style="color:#ffd8bf;margin:8px 0 0;font-size:14px;">宇聯科技股份有限公司</p>
  </div>
  <div style="padding:30px;">
    <p style="font-size:16px;"><strong>{{company_name}}</strong> {{contact_person}} 您好！</p>
    <p style="line-height:1.8;">
      提醒您，「<strong>{{case_title}}</strong>」（案號 <strong>{{case_number}}</strong>）意向書尚未完成填寫。
      為保障貴公司的投標權益，請儘速點擊下方按鈕完成填寫。
    </p>
    <div style="background:#fff1f0;padding:16px;border-radius:6px;margin:20px 0;border-left:4px solid #ff4d4f;">
      <strong>請注意：</strong>若未於期限內完成意向書填寫，將無法取得價格承諾書。
    </div>
    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:30px 0;">
      <tr><td align="center">
        <table cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td align="center" bgcolor="#cf1322" style="border-radius:6px;mso-padding-alt:14px 40px;" height="48">
              <a href="{{survey_link}}" target="_blank" style="display:inline-block;padding:14px 40px;color:#ffffff;font-size:16px;font-weight:bold;text-decoration:none;font-family:Arial,sans-serif;line-height:20px;mso-line-height-rule:exactly;">
                立即填寫意向書
              </a>
            </td>
          </tr>
        </table>
      </td></tr>
    </table>
    <hr style="border:none;border-top:1px solid #e8e8e8;margin:30px 0;"/>
    <p style="font-size:14px;color:#666;line-height:1.8;">
      如有任何疑問，請洽：<br/>
      周伶瑋 (Jolie) 02-8773-0263 #181 / <a href="mailto:jolie.chou@ulink.com.tw">jolie.chou@ulink.com.tw</a><br/>
      蘇鈺涵 (Anny) 02-8773-0263 #182 / <a href="mailto:anny.su@ulink.com.tw">anny.su@ulink.com.tw</a>
    </p>
    <p style="margin-top:20px;"><strong>宇聯科技 敬上</strong></p>
  </div>
</div>
</body>
</html>`,
  },
  custom_blank: {
    name: '空白範本',
    subject: '',
    body: '',
  },
};

const STATUS_OPTIONS = [
  { value: '未發送', label: '未發送' },
  { value: '已發送未讀', label: '已發送未讀（全部）' },
  { value: '被退信', label: '↳ 被退信' },
  { value: '疑似進垃圾桶', label: '↳ 疑似進垃圾桶' },
  { value: '被無視', label: '↳ 被無視' },
  { value: '已讀取未填', label: '已讀取未填' },
  { value: '已回覆意願', label: '已回覆意願' },
];

function EmailComposePage() {
  // Campaign
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Dealers
  const [dealers, setDealers] = useState([]);
  const [dealersLoading, setDealersLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedDealerKeys, setSelectedDealerKeys] = useState([]);

  // Email content
  const [subject, setSubject] = useState(EMAIL_TEMPLATES.loi_initial.subject);
  const [htmlBody, setHtmlBody] = useState(EMAIL_TEMPLATES.loi_initial.body);
  const [selectedTemplate, setSelectedTemplate] = useState('loi_initial');

  // UI state
  const [sending, setSending] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('compose');

  // Send history
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);

  // Load campaigns on mount
  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const res = await loiAPI.getCampaigns();
      const list = res.data || [];
      setCampaigns(list);
      // Auto-select first campaign
      if (list.length > 0 && !selectedCampaign) {
        setSelectedCampaign(list[0].id);
      }
    } catch (error) {
      message.error('載入活動列表失敗');
    }
  };

  // Load dealers when campaign changes
  useEffect(() => {
    if (selectedCampaign) {
      loadDealers();
      loadEmailLogs();
    } else {
      setDealers([]);
      setEmailLogs([]);
    }
  }, [selectedCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDealers = async () => {
    if (!selectedCampaign) return;
    setDealersLoading(true);
    try {
      // Use getDealerStatus which includes status field for filtering
      const res = await loiAPI.getDealerStatus(selectedCampaign);
      const list = res.data || [];
      setDealers(list);
      // Select all by default
      setSelectedDealerKeys(list.map((d) => d.id));
    } catch (error) {
      message.error('載入經銷商失敗');
    } finally {
      setDealersLoading(false);
    }
  };

  const loadEmailLogs = async () => {
    if (!selectedCampaign) return;
    setEmailLogsLoading(true);
    try {
      const res = await loiAPI.getEmailLogs(selectedCampaign);
      setEmailLogs(res.data || []);
    } catch (error) {
      message.error('載入發信記錄失敗');
    } finally {
      setEmailLogsLoading(false);
    }
  };

  // Filtered dealers based on status + search text
  const filteredDealers = useMemo(() => {
    let list = dealers;
    if (statusFilter.length > 0) {
      list = list.filter((d) => {
        // Direct status match
        if (statusFilter.includes(d.status)) return true;
        // "已發送未讀" parent filter matches all unread sub-reasons
        if (statusFilter.includes('已發送未讀') && d.status === '已發送未讀') return true;
        // Sub-reason filter (被退信, 疑似進垃圾桶, 被無視)
        if (d.status === '已發送未讀' && d.unread_reason && statusFilter.includes(d.unread_reason)) return true;
        return false;
      });
    }
    if (searchText.trim()) {
      const kw = searchText.trim().toLowerCase();
      list = list.filter((d) =>
        (d.company_name || '').toLowerCase().includes(kw) ||
        (d.contact_person || '').toLowerCase().includes(kw) ||
        (d.contact_email || '').toLowerCase().includes(kw)
      );
    }
    return list;
  }, [dealers, statusFilter, searchText]);

  // Recipient count = selected dealers
  const recipientCount = selectedDealerKeys.length;

  // Whether all visible dealers are selected
  const allVisibleSelected = useMemo(() => {
    if (filteredDealers.length === 0) return false;
    return filteredDealers.every((d) => selectedDealerKeys.includes(d.id));
  }, [filteredDealers, selectedDealerKeys]);

  // Toggle select all visible dealers
  const handleSelectAll = (checked) => {
    if (checked) {
      // Add all filtered dealer IDs to selection
      const filteredIds = filteredDealers.map((d) => d.id);
      setSelectedDealerKeys((prev) => {
        const set = new Set([...prev, ...filteredIds]);
        return [...set];
      });
    } else {
      // Remove all filtered dealer IDs from selection
      const filteredIds = new Set(filteredDealers.map((d) => d.id));
      setSelectedDealerKeys((prev) => prev.filter((id) => !filteredIds.has(id)));
    }
  };

  // Preview HTML with sample data replaced
  const previewHtml = useMemo(() => {
    let html = htmlBody;
    Object.entries(SAMPLE_DATA).forEach(([key, value]) => {
      html = html.replaceAll(key, value);
    });
    return html;
  }, [htmlBody]);

  const handleCampaignChange = (campaignId) => {
    setSelectedCampaign(campaignId);
    setSelectedDealerKeys([]);
    setStatusFilter([]);
    setSearchText('');
  };

  const handleTemplateChange = (key) => {
    setSelectedTemplate(key);
    const tpl = EMAIL_TEMPLATES[key];
    if (tpl) {
      setSubject(tpl.subject);
      setHtmlBody(tpl.body);
    }
  };

  const handleSend = async () => {
    setConfirmVisible(false);
    setSending(true);
    try {
      const dealerIds = selectedDealerKeys;

      if (dealerIds.length === 0) {
        message.warning('沒有選取任何收件人');
        setSending(false);
        return;
      }

      const res = await tenderAPI.sendCustomEmail(selectedCampaign, {
        subject,
        html_body: htmlBody,
        dealer_ids: dealerIds,
      });
      message.success(`發送完成，共寄出 ${res.data?.sent || dealerIds.length} 封`);
      loadEmailLogs();
    } catch (error) {
      message.error(`發送失敗: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendClick = () => {
    if (!selectedCampaign) {
      message.warning('請先選擇活動');
      return;
    }
    if (!subject.trim()) {
      message.warning('請輸入郵件主旨');
      return;
    }
    if (!htmlBody.trim()) {
      message.warning('請輸入郵件內容');
      return;
    }
    setConfirmVisible(true);
  };

  // Dealer table columns for recipient selection
  const dealerColumns = [
    {
      title: '公司 / 聯絡人', key: 'company', ellipsis: true,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.company_name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.contact_person}</div>
        </div>
      ),
    },
    { title: 'Email', dataIndex: 'contact_email', key: 'contact_email', ellipsis: true },
    {
      title: '狀態', key: 'status', width: 110, align: 'center',
      render: (_, record) => {
        if (record.status === '已發送未讀' && record.unread_reason) {
          const reasonColorMap = {
            '被退信': 'error',
            '疑似進垃圾桶': 'warning',
            '被無視': 'default',
          };
          const tag = <Tag color={reasonColorMap[record.unread_reason] || 'processing'}>{record.unread_reason}</Tag>;
          return record.handling_suggestion
            ? <Tooltip title={record.handling_suggestion}>{tag}</Tooltip>
            : tag;
        }
        const colorMap = { '未發送': 'default', '已發送未讀': 'processing', '已讀取未填': 'warning', '已回覆意願': 'success' };
        return record.status ? <Tag color={colorMap[record.status] || 'default'}>{record.status}</Tag> : '-';
      },
    },
  ];

  // Email log columns
  const emailLogColumns = [
    {
      title: '經銷商',
      key: 'dealer_name',
      width: 160,
      ellipsis: true,
      render: (_, record) => record.dealer?.company_name || '-',
    },
    {
      title: 'Email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (_, record) => record.dealer?.contact_email || '-',
    },
    {
      title: '類型', dataIndex: 'email_type', key: 'email_type', width: 100,
      render: (type) => {
        const map = { initial: '初次發送', reminder: '催寄', custom: '自訂郵件', tender_notify: '招標通知', tender_reminder: '招標催寄' };
        const colorMap = { initial: 'blue', reminder: 'orange', custom: 'purple', tender_notify: 'cyan', tender_reminder: 'gold' };
        return <Tag color={colorMap[type] || 'default'}>{map[type] || type}</Tag>;
      },
    },
    {
      title: '發送時間', dataIndex: 'sent_at', key: 'sent_at', width: 180,
      render: (val) => val ? new Date(val).toLocaleString('zh-TW') : '-',
    },
    {
      title: '狀態', dataIndex: 'status', key: 'status', width: 80,
      render: (status) => {
        const map = {
          sent: { color: 'green', text: '成功' },
          failed: { color: 'red', text: '失敗' },
        };
        const info = map[status] || { color: 'default', text: status || '-' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
  ];

  const renderComposeTab = () => (
    <Row gutter={16}>
      {/* Left: Editor */}
      <Col xs={24} lg={14}>
        {/* SMTP Info */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
          message={
            <span>
              寄件者：<strong>宇聯科技 Jolie</strong> (jolie@comboware.io) &middot;
              回覆至：jolie.chou@ulink.com.tw
            </span>
          }
        />

        {/* Template Selector */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space>
            <span style={{ fontWeight: 500 }}>選擇範本：</span>
            <Select
              value={selectedTemplate}
              onChange={handleTemplateChange}
              style={{ width: 280 }}
            >
              {Object.entries(EMAIL_TEMPLATES).map(([key, tpl]) => (
                <Option key={key} value={key}>{tpl.name}</Option>
              ))}
            </Select>
          </Space>
        </Card>

        <Card title="郵件內容" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>主旨</label>
              <Input
                placeholder="例：宇聯科技_{{case_title}} 意向書調查"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                size="large"
              />
            </div>
            <div>
              <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>HTML 內容</label>
              <TextArea
                placeholder="輸入 HTML 郵件內容..."
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                rows={16}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
            </div>
          </Space>
        </Card>

        {/* Template Variables Reference */}
        <Card title="範本變數參考" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[8, 8]}>
            {TEMPLATE_VARIABLES.map((v) => (
              <Col key={v.key} xs={12} sm={8}>
                <Tag
                  color="geekblue"
                  style={{ cursor: 'pointer', marginBottom: 4 }}
                  onClick={() => {
                    setHtmlBody((prev) => prev + v.key);
                  }}
                >
                  {v.key}
                </Tag>
                <span style={{ fontSize: 12, color: '#888' }}>{v.desc}</span>
              </Col>
            ))}
          </Row>
          <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
            點擊變數可自動插入至內容末端
          </div>
        </Card>
      </Col>

      {/* Right: Preview + Recipients */}
      <Col xs={24} lg={10}>
        {/* Preview */}
        <Card
          title={
            <Space>
              <EyeOutlined />
              預覽 (範例資料)
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          {subject && (
            <div style={{ marginBottom: 8 }}>
              <strong>主旨：</strong>{' '}
              {Object.entries(SAMPLE_DATA).reduce(
                (s, [k, v]) => s.replaceAll(k, v),
                subject
              )}
            </div>
          )}
          <Divider style={{ margin: '8px 0' }} />
          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: 0,
              minHeight: 200,
              maxHeight: 500,
              overflow: 'auto',
              backgroundColor: '#fff',
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml || '<p style="color:#ccc;padding:16px">在左側輸入 HTML 內容後即可預覽</p>' }}
          />
        </Card>

        {/* Unread Breakdown */}
        {dealers.length > 0 && (() => {
          const bouncedCount = dealers.filter(d => d.status === '已發送未讀' && d.unread_reason === '被退信').length;
          const spamCount = dealers.filter(d => d.status === '已發送未讀' && d.unread_reason === '疑似進垃圾桶').length;
          const ignoredCount = dealers.filter(d => d.status === '已發送未讀' && d.unread_reason === '被無視').length;
          const totalUnread = bouncedCount + spamCount + ignoredCount;
          if (totalUnread === 0) return null;
          return (
            <Card size="small" title="📊 未讀取原因分析" style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={8}>
                  <div
                    style={{ textAlign: 'center', padding: '8px 4px', background: '#fff2f0', borderRadius: 6, cursor: 'pointer', border: statusFilter.includes('被退信') ? '2px solid #ff4d4f' : '2px solid transparent' }}
                    onClick={() => setStatusFilter(['被退信'])}
                  >
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#cf1322' }}>{bouncedCount}</div>
                    <Tag color="error">被退信</Tag>
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>確認 Email 正確性</div>
                  </div>
                </Col>
                <Col span={8}>
                  <div
                    style={{ textAlign: 'center', padding: '8px 4px', background: '#fffbe6', borderRadius: 6, cursor: 'pointer', border: statusFilter.includes('疑似進垃圾桶') ? '2px solid #faad14' : '2px solid transparent' }}
                    onClick={() => setStatusFilter(['疑似進垃圾桶'])}
                  >
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#d48806' }}>{spamCount}</div>
                    <Tag color="warning">疑似進垃圾桶</Tag>
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>請加入白名單</div>
                  </div>
                </Col>
                <Col span={8}>
                  <div
                    style={{ textAlign: 'center', padding: '8px 4px', background: '#f5f5f5', borderRadius: 6, cursor: 'pointer', border: statusFilter.includes('被無視') ? '2px solid #8c8c8c' : '2px solid transparent' }}
                    onClick={() => setStatusFilter(['被無視'])}
                  >
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#595959' }}>{ignoredCount}</div>
                    <Tag>被無視</Tag>
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>發送催寄提醒</div>
                  </div>
                </Col>
              </Row>
            </Card>
          );
        })()}

        {/* Recipients */}
        <Card title="收件人設定" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={selectedDealerKeys.length > 0 && !allVisibleSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                全選 ({filteredDealers.length} 人)
              </Checkbox>
              <Tag color="blue" style={{ fontSize: 14 }}>已選：{recipientCount} / {dealers.length} 人</Tag>
            </div>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="搜尋公司名稱、聯絡人、Email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>依狀態篩選顯示：</label>
              <Select
                mode="multiple"
                placeholder="全部狀態"
                value={statusFilter}
                onChange={(vals) => setStatusFilter(vals)}
                style={{ width: '100%' }}
                allowClear
              >
                {STATUS_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
            </div>
            <Table
              columns={dealerColumns}
              dataSource={filteredDealers}
              rowKey="id"
              loading={dealersLoading}
              size="small"
              pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 筆` }}
              rowSelection={{
                selectedRowKeys: selectedDealerKeys,
                onChange: (keys) => {
                  // Merge: keep selections from non-visible dealers, apply new keys for visible ones
                  const filteredIds = new Set(filteredDealers.map((d) => d.id));
                  const kept = selectedDealerKeys.filter((id) => !filteredIds.has(id));
                  setSelectedDealerKeys([...kept, ...keys]);
                },
              }}
            />
          </Space>
        </Card>

        {/* Send */}
        <Button
          type="primary"
          icon={<SendOutlined />}
          size="large"
          block
          loading={sending}
          disabled={!selectedCampaign || !subject.trim() || !htmlBody.trim()}
          onClick={handleSendClick}
          style={{
            height: 48,
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 16,
            background: recipientCount > 0 ? 'linear-gradient(135deg, #1890ff, #36cfc9)' : undefined,
            border: 'none',
          }}
        >
          發送郵件 ({recipientCount} 人)
        </Button>
      </Col>
    </Row>
  );

  const renderHistoryTab = () => (
    <Card
      title="發信記錄"
      extra={
        <Button icon={<ReloadOutlined />} onClick={loadEmailLogs} disabled={!selectedCampaign}>
          重新整理
        </Button>
      }
    >
      <Table
        columns={emailLogColumns}
        dataSource={emailLogs}
        rowKey="id"
        loading={emailLogsLoading}
        size="small"
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 筆` }}
      />
    </Card>
  );

  const tabItems = [
    { key: 'compose', label: '撰寫郵件', children: renderComposeTab() },
    { key: 'history', label: `發送記錄 (${emailLogs.length})`, children: renderHistoryTab() },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>信件發送</h2>

      <Card style={{ marginBottom: 16 }}>
        <Space>
          <span style={{ fontWeight: 500 }}>選擇活動：</span>
          <Select
            placeholder="選擇活動"
            value={selectedCampaign}
            onChange={handleCampaignChange}
            style={{ width: 400 }}
            allowClear
          >
            {campaigns.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.case_number ? `[${c.case_number}] ` : ''}{c.title}{c.year ? ` (${c.year})` : ''}
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      <Tabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} />

      {/* Confirmation Modal */}
      <Modal
        title="確認發送"
        open={confirmVisible}
        onOk={handleSend}
        onCancel={() => setConfirmVisible(false)}
        okText="確認發送"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>即將發送郵件：</p>
        <ul>
          <li><strong>寄件者：</strong>宇聯科技 Jolie (jolie@comboware.io)</li>
          <li><strong>活動：</strong>{campaigns.find((c) => c.id === selectedCampaign)?.title || '-'}</li>
          <li><strong>主旨：</strong>{subject}</li>
          <li><strong>收件人數：</strong>{recipientCount} 人</li>
        </ul>
        <p style={{ color: '#ff4d4f' }}>發送後無法撤回，請確認內容無誤。</p>
      </Modal>
    </div>
  );
}

export default EmailComposePage;
